import { Ban, Check, History, KeyRound, ShieldCheck, UserPlus } from 'lucide-react';

import { AVISO_DE_ACAO_INDISPONIVEL } from '@/modulos/operacao/comum/cadastroIndisponivel';
import { avisarIndisponivel } from '@/modulos/portal/credenciamento/indisponivel';
import { CopyableText } from '@/ui/CopyableText';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { Spinner } from '@/ui/Spinner';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/base/table';

import type {
  OperadorDelegado,
  PerfilDoOperadorDelegado,
  PermissaoDelegada,
  SituacaoDoOperadorDelegado,
} from './contrato';
import { useOperadoresDelegados } from './dados';

const ROTULO_DO_PERFIL: Record<PerfilDoOperadorDelegado, string> = {
  operador: 'Operador',
  financeiro_delegado: 'Financeiro delegado',
};

/** Abreviações do protótipo, na ordem em que ele as lista. */
const ROTULO_DA_PERMISSAO: Record<PermissaoDelegada, string> = {
  documentos: 'Docs',
  cadastros: 'Cadastros',
  solicitacoes: 'Solicit.',
  demonstrativos: 'Demonstr.',
};

const SITUACAO: Record<
  SituacaoDoOperadorDelegado,
  { rotulo: string; variante: 'default' | 'outline' | 'destructive' }
> = {
  ativo: { rotulo: 'Ativo', variante: 'default' },
  aguardando_aceite: { rotulo: 'Aguardando aceite', variante: 'outline' },
  revogado: { rotulo: 'Revogado', variante: 'destructive' },
};

interface OperadoresDelegadosCardProps {
  /** Quem administra a empresa credenciada — só essa pessoa vê Convidar e Revogar. */
  podeGerenciar: boolean;
}

/**
 * Card "Operadores delegados (acesso restrito)" do protótipo.
 *
 * NADA GRAVA. Não há tabela de delegação nem rota em `apps/api/src/modulos/`:
 * Convidar, Revogar e Auditoria mostram o aviso de indisponibilidade e não
 * alteram a lista, nem em memória. Copiar a TAG é ação local e funciona.
 *
 * O aviso sai pelo `sonner`, como no protótipo: o `<Toaster />` montado em
 * `App.tsx` é o dele, e um `useToast()` do shadcn não apareceria na tela.
 */
export function OperadoresDelegadosCard({ podeGerenciar }: OperadoresDelegadosCardProps) {
  const { data: operadores, isLoading, isError, refetch } = useOperadoresDelegados();
  const avisar = () => avisarIndisponivel(AVISO_DE_ACAO_INDISPONIVEL);

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 space-y-0 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1.5">
          <CardTitle asChild>
            <h2 className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              Operadores delegados (acesso restrito)
            </h2>
          </CardTitle>
          <CardDescription>
            Convide operadores ou financeiro delegado com permissões específicas. Cada convite gera
            um TAG de validação que o operador precisa informar no primeiro acesso.
          </CardDescription>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button variant="outline" size="sm" onClick={avisar}>
            <History className="h-4 w-4" aria-hidden="true" />
            Auditoria
          </Button>
          {podeGerenciar && (
            <Button size="sm" onClick={avisar}>
              <UserPlus className="h-4 w-4" aria-hidden="true" />
              Convidar
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <Spinner size="sm" />
        ) : isError ? (
          <ErrorState
            variant="load-failed"
            description="Não foi possível carregar os operadores delegados agora. Tente novamente em instantes."
            primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
          />
        ) : !operadores || operadores.length === 0 ? (
          <EmptyState
            icon={ShieldCheck}
            title="Nenhum operador delegado"
            description="Quando você convidar alguém com acesso restrito, a pessoa aparece aqui."
          />
        ) : (
          <Table aria-label="Operadores delegados">
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead>Perfil</TableHead>
                <TableHead>Permissões</TableHead>
                <TableHead>TAG</TableHead>
                <TableHead>Status</TableHead>
                {podeGerenciar && <TableHead className="text-right">Ações</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {operadores.map((operador) => (
                <LinhaDoOperador
                  key={operador.id}
                  operador={operador}
                  podeGerenciar={podeGerenciar}
                  onRevogar={avisar}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function LinhaDoOperador({
  operador,
  podeGerenciar,
  onRevogar,
}: {
  operador: OperadorDelegado;
  podeGerenciar: boolean;
  onRevogar: () => void;
}) {
  const situacao = SITUACAO[operador.situacao];

  return (
    <TableRow>
      <TableCell className="font-medium">{operador.nome}</TableCell>
      <TableCell className="text-muted-foreground">{operador.email}</TableCell>
      <TableCell>
        <Badge variant="secondary" className="whitespace-nowrap">
          {ROTULO_DO_PERFIL[operador.perfil]}
        </Badge>
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
        {operador.permissoes.map((permissao) => ROTULO_DA_PERMISSAO[permissao]).join(' · ')}
      </TableCell>
      <TableCell>
        {operador.tag_validada ? (
          <Badge variant="outline" className="gap-1 whitespace-nowrap">
            <Check className="h-3 w-3" aria-hidden="true" />
            Validado
          </Badge>
        ) : (
          <span className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5">
            <KeyRound className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            <CopyableText value={operador.tag} label={`Copiar TAG de ${operador.nome}`} />
          </span>
        )}
      </TableCell>
      <TableCell>
        <Badge variant={situacao.variante} className="whitespace-nowrap">
          {situacao.rotulo}
        </Badge>
      </TableCell>
      {podeGerenciar && (
        <TableCell className="text-right">
          {operador.situacao !== 'revogado' && (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive-text hover:text-destructive-text"
              onClick={onRevogar}
            >
              <Ban className="h-4 w-4" aria-hidden="true" />
              Revogar
            </Button>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}
